import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { X } from "lucide-react";

const AuthModal = ({ onClose, onSuccess, defaultTab = "login" }) => {
  const [isLogin, setIsLogin] = useState(defaultTab === "login");
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    full_name: "",
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const loginFormData = new FormData();
        loginFormData.append("username", formData.username);
        loginFormData.append("password", formData.password);

        const response = await axios.post(`${API}/auth/login`, loginFormData, {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        });

        localStorage.setItem("token", response.data.access_token);
        toast.success("Welcome back!");
        onSuccess(response.data.user);
      } else {
        await axios.post(`${API}/auth/register`, {
          username: formData.username,
          email: formData.email,
          password: formData.password,
          full_name: formData.full_name,
        });

        toast.success("Account created! Please sign in.");
        setIsLogin(true);
        setFormData({ ...formData, password: "" });
      }
    } catch (error) {
      console.error("Auth error:", error);
      toast.error(error.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="auth-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-200">
        {/* Header with close button */}
        <div className="relative p-6 pb-0">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
            data-testid="auth-modal-close"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="text-center mb-6">
            <h2 className="text-2xl font-semibold text-gray-900">
              {isLogin ? "Sign In" : "Create Account"}
            </h2>
            <p className="text-gray-500 text-sm mt-1">
              {isLogin ? "Welcome back to ScoreXprt" : "Join ScoreXprt for ad-free experience"}
            </p>
          </div>

          {/* Tab Toggle */}
          <div className="flex bg-gray-100 p-1 rounded-lg mb-6">
            <button
              type="button"
              onClick={() => setIsLogin(true)}
              className={`flex-1 py-2.5 text-sm font-medium rounded-md transition-all ${
                isLogin
                  ? "bg-white shadow-sm text-gray-900"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              data-testid="auth-signin-tab"
            >
              Sign In
            </button>
            <button
              type="button"
              onClick={() => setIsLogin(false)}
              className={`flex-1 py-2.5 text-sm font-medium rounded-md transition-all ${
                !isLogin
                  ? "bg-white shadow-sm text-gray-900"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              data-testid="auth-register-tab"
            >
              Register
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 pt-0 space-y-4">
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1.5">
              Username
            </label>
            <input
              id="username"
              name="username"
              type="text"
              value={formData.username}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#00bf63]/20 focus:border-[#00bf63] transition-colors"
              placeholder="Enter your username"
              data-testid="auth-username-input"
            />
          </div>

          {!isLogin && (
            <>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#00bf63]/20 focus:border-[#00bf63] transition-colors"
                  placeholder="Enter your email"
                  data-testid="auth-email-input"
                />
              </div>
              <div>
                <label htmlFor="full_name" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Full Name <span className="text-gray-400">(optional)</span>
                </label>
                <input
                  id="full_name"
                  name="full_name"
                  type="text"
                  value={formData.full_name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#00bf63]/20 focus:border-[#00bf63] transition-colors"
                  placeholder="Enter your full name"
                  data-testid="auth-fullname-input"
                />
              </div>
            </>
          )}

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5">
              Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              required
              minLength={6}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#00bf63]/20 focus:border-[#00bf63] transition-colors"
              placeholder={isLogin ? "Enter your password" : "Create a password (min 6 characters)"}
              data-testid="auth-password-input"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-[#00bf63] text-white font-medium rounded-xl hover:bg-[#00a854] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="auth-submit-btn"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                Processing...
              </span>
            ) : (
              isLogin ? "Sign In" : "Create Account"
            )}
          </button>

          {isLogin && (
            <p className="text-center text-sm text-gray-500">
              Don't have an account?{" "}
              <button
                type="button"
                onClick={() => setIsLogin(false)}
                className="text-[#00bf63] font-medium hover:text-[#00a854]"
                data-testid="auth-toggle-btn"
              >
                Register
              </button>
            </p>
          )}

          {!isLogin && (
            <p className="text-center text-sm text-gray-500">
              Already have an account?{" "}
              <button
                type="button"
                onClick={() => setIsLogin(true)}
                className="text-[#00bf63] font-medium hover:text-[#00a854]"
              >
                Sign In
              </button>
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

export default AuthModal;
